import { CircleCheckIcon, TrendingDownIcon, TrendingUpIcon, type LucideIcon } from 'lucide-react';
import { IconBadge } from '@monksflow/monks-ui';

import { AnalysisMarkdown, AnalysisSubHeader } from '../../../components';
import type { CultureContextData } from '../CultureContext.types';
import { useTranslation } from 'react-i18next';

interface CulturalSignalsProps {
  culturalSignals?: CultureContextData['culturalSignals'];
}

/** The design gives each direction its own badge colour. */
const BADGE_CLASS = {
  emerging: 'bg-badge-lime text-badge-lime-foreground',
  mainstream: 'bg-badge-blue text-badge-blue-foreground',
  declining: 'bg-badge-red text-badge-red-foreground',
} as const;

export const CulturalSignals = ({ culturalSignals }: CulturalSignalsProps) => {
  const { t } = useTranslation('analysis');

  // A signal is one paragraph now. Reports written before the redesign hold the list of
  // bullets it replaced, which reads as a paragraph once joined.
  const asProse = (signal?: string | string[]): string =>
    Array.isArray(signal) ? signal.join(' ') : (signal ?? '');

  const emerging = asProse(culturalSignals?.emerging);
  const mainstream = asProse(culturalSignals?.mainstream);
  const declining = asProse(culturalSignals?.declining);
  if (!emerging && !mainstream && !declining) return null;

  const signals: { key: string; icon: LucideIcon; badgeClass: string; text: string }[] = [
    {
      key: t('analysisResult.culturalSignals.emerging'),
      icon: TrendingUpIcon,
      badgeClass: BADGE_CLASS.emerging,
      text: emerging,
    },
    {
      key: t('analysisResult.culturalSignals.mainstream'),
      icon: CircleCheckIcon,
      badgeClass: BADGE_CLASS.mainstream,
      text: mainstream,
    },
    {
      key: t('analysisResult.culturalSignals.declining'),
      icon: TrendingDownIcon,
      badgeClass: BADGE_CLASS.declining,
      text: declining,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.culturalSignals.culturalSignals')}
        subtitle={t('analysisResult.culturalSignals.whereBehavioursAreHeading')}
        subtitleTone="meta"
      />
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-3">
        {signals
          .filter(({ text }) => text !== '')
          .map(({ key, icon: Icon, badgeClass, text }) => (
            <div
              key={key}
              className="flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl"
            >
              <IconBadge className={badgeClass}>
                <Icon />
              </IconBadge>
              <div className="flex flex-col gap-2">
                <span className="text-body-xl font-medium text-foreground">{key}</span>
                <AnalysisMarkdown text={text} className="text-body-md text-foreground-alt" />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
